import { Clock, AlertCircle, Gauge, Timer } from 'lucide-react'

type SettingsData = {
  estimatedDuration: number | null
  emergencyStartTime: string | null
  emergencyEndTime: string | null
  totalLength: string | null
  difficulty: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'EXPERT'
}

type SettingsOverviewProps = {
  settings: SettingsData
}

function getDifficultyColor(difficulty: string) {
  switch (difficulty) {
    case 'BEGINNER':
      return 'bg-green-100 text-green-700'
    case 'INTERMEDIATE':
      return 'bg-blue-100 text-blue-700'
    case 'ADVANCED':
      return 'bg-orange-100 text-orange-700'
    case 'EXPERT':
      return 'bg-red-100 text-red-700'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

function formatDuration(minutes: number | null): string {
  if (!minutes) return 'Not set'
  if (minutes < 60) return `${minutes} minutes`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (mins === 0) return `${hours} hour${hours > 1 ? 's' : ''}`
  return `${hours}h ${mins}m`
}

export function SettingsOverview({ settings }: SettingsOverviewProps) {
  const items = [
    {
      icon: Clock,
      label: 'Estimated Duration',
      value: formatDuration(settings.estimatedDuration),
      color: 'text-blue-600',
    },
    {
      icon: Timer,
      label: 'Total Length',
      value: settings.totalLength || 'Not set',
      color: 'text-indigo-600',
    },
    {
      icon: AlertCircle,
      label: 'Emergency Window',
      value:
        settings.emergencyStartTime && settings.emergencyEndTime
          ? `${settings.emergencyStartTime} - ${settings.emergencyEndTime}`
          : 'Not set',
      color: 'text-orange-600',
    },
    {
      icon: Gauge,
      label: 'Difficulty',
      value: settings.difficulty,
      color: 'text-purple-600',
      badge: true,
    },
  ]

  return (
    <div className="p-4">
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200">
          {items.map(({ icon: Icon, label, value, color, badge }) => (
            <div key={label} className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-5 h-5 ${color}`} />
                <span className="text-sm font-medium text-gray-500">{label}</span>
              </div>
              {badge ? (
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(
                    value
                  )}`}
                >
                  {value}
                </span>
              ) : (
                <p className="text-lg font-semibold text-gray-900">{value}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
